import { ChevronLeft } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { StatTiles, type Tile } from '@/components/data/StatTiles'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { UserActionDialog } from '@/components/UserActionDialog'
import ProjectCustomizations, { type CustomizationsState } from '@/pages/Projects/ProjectCustomizations'
import { AutomationSection, type ProjectAutomation } from '@/pages/Projects/sections/AutomationSection'
import {
  ComponentsSection,
  VersionsSection,
  type Component,
  type Version,
} from '@/pages/Projects/sections/MetaSections'
import { RolesSection, type Role } from '@/pages/Projects/sections/RolesSection'
import { AdminActions, SchemesSection } from '@/pages/Projects/sections/SchemesAdminSections'
import type { DialogState } from '@/pages/Projects/sections/types'
import { api } from '@/lib/api'
import { runProjectCustomizations } from '@/lib/customizations'

const TABS = [
  { key: 'roles', label: 'Roles' },
  { key: 'versions', label: 'Versions' },
  { key: 'components', label: 'Components' },
  { key: 'automation', label: 'Automation' },
  { key: 'schemes', label: 'Schemes' },
  { key: 'customizations', label: 'Customizations' },
] as const
type TabKey = (typeof TABS)[number]['key']
const toTab = (v: string | null): TabKey => TABS.find((t) => t.key === v)?.key ?? 'roles'

const PANEL_ID = 'project-panel'
const TAB_PREFIX = 'project-tab'

type Scheme = { name: string } | null | undefined

/** Tile counter that never throws: data of an unexpected shape marks the tile failed. */
function listTile(key: TabKey, label: string, data: unknown, err: boolean, meta: (n: unknown[]) => string): Tile {
  if (err) return { key, label, value: null, state: 'failed', meta: 'not loaded' }
  if (data === null) return { key, label, value: null, state: 'loading' }
  if (!Array.isArray(data)) return { key, label, value: null, state: 'failed', meta: 'unexpected data' }
  return { key, label, value: data.length, meta: meta(data) }
}

export default function ProjectDetail() {
  const { projectKey = '' } = useParams()
  const location = useLocation() as { state?: { project?: { key: string; name: string } } }
  const projectName = location.state?.project?.name ?? projectKey
  const [params, setParams] = useSearchParams()
  const tab = toTab(params.get('tab'))
  const go = (t: string) => setParams((p) => {
    const next = new URLSearchParams(p)
    next.set('tab', t)
    next.delete('sel')
    return next
  }, { replace: true })

  const [versions, setVersions] = useState<Version[] | null>(null)
  const [versionsErr, setVersionsErr] = useState(false)
  const [components, setComponents] = useState<Component[] | null>(null)
  const [componentsErr, setComponentsErr] = useState(false)
  const [roles, setRoles] = useState<Role[] | null>(null)
  const [rolesErr, setRolesErr] = useState(false)
  const [automation, setAutomation] = useState<ProjectAutomation | null>(null)
  const [automationErr, setAutomationErr] = useState(false)
  const [custom, setCustom] = useState<CustomizationsState>({ status: 'idle' })

  const [dialog, setDialog] = useState<DialogState | null>(null)

  // scheme reads: undefined = loading, null = error/not found, {name} = loaded
  const [permScheme, setPermScheme] = useState<Scheme>(undefined)
  const [notifScheme, setNotifScheme] = useState<Scheme>(undefined)
  const [secScheme, setSecScheme] = useState<Scheme>(undefined)
  const [priScheme, setPriScheme] = useState<Scheme>(undefined)
  const [projectId, setProjectId] = useState<string | null>(null)

  const refreshVersions = useCallback(() => {
    setVersions(null)
    setVersionsErr(false)
    api<Version[]>(`/projects/${encodeURIComponent(projectKey)}/versions`)
      .then(setVersions)
      .catch(() => setVersionsErr(true))
  }, [projectKey])

  const refreshComponents = useCallback(() => {
    setComponents(null)
    setComponentsErr(false)
    api<Component[]>(`/projects/${encodeURIComponent(projectKey)}/components`)
      .then(setComponents)
      .catch(() => setComponentsErr(true))
  }, [projectKey])

  const refreshRoles = useCallback(() => {
    setRoles(null)
    setRolesErr(false)
    api<Role[]>(`/projects/${encodeURIComponent(projectKey)}/roles`)
      .then(setRoles)
      .catch(() => setRolesErr(true))
  }, [projectKey])

  const refreshAutomation = useCallback(() => {
    setAutomation(null)
    setAutomationErr(false)
    api<ProjectAutomation>(`/projects/${encodeURIComponent(projectKey)}/automation`)
      .then(setAutomation)
      .catch(() => setAutomationErr(true))
  }, [projectKey])

  // scheme reads — graceful on error; re-run after a scheme change so the displayed value updates.
  const refreshSchemes = useCallback(() => {
    type R = { findings: { name?: string }[] }
    const post = <T,>(op: string, body: Record<string, unknown>) =>
      api<T>(`/ops/${op}`, { method: 'POST', body: JSON.stringify(body) })
    const named = (r: R) => (r.findings[0]?.name ? { name: r.findings[0].name } : null)

    setPermScheme(undefined)
    post<R>('projects.get_assigned_permission_scheme', { project_key: projectKey })
      .then((r) => setPermScheme(named(r)))
      .catch(() => setPermScheme(null))

    setNotifScheme(undefined)
    post<R>('projects.get_project_notification_scheme', { project_key: projectKey })
      .then((r) => setNotifScheme(named(r)))
      .catch(() => setNotifScheme(null))

    setSecScheme(undefined)
    post<R>('projects.get_project_issue_security_scheme', { project_key: projectKey })
      .then((r) => setSecScheme(named(r)))
      .catch(() => setSecScheme(null))

    setPriScheme(undefined)
    ;(async () => {
      try {
        const p = await post<{ findings: { id?: string }[] }>('projects.get_project', {
          project_key: projectKey,
        })
        const pid = p.findings[0]?.id ?? null
        setProjectId(pid)
        if (pid) {
          const pr = await post<R>('workflows.list_priority_schemes', { project_id: pid })
          setPriScheme(named(pr))
        } else setPriScheme(null)
      } catch {
        setProjectId(null)
        setPriScheme(null)
      }
    })()
  }, [projectKey])

  const runCustomizations = useCallback(async () => {
    setCustom({ status: 'loading' })
    try {
      setCustom({ status: 'done', report: await runProjectCustomizations(projectKey) })
    } catch (e) {
      setCustom({ status: 'error', error: (e as Error).message })
    }
  }, [projectKey])

  useEffect(() => {
    refreshVersions()
    refreshComponents()
    refreshRoles()
    refreshAutomation()
    refreshSchemes()
    setCustom({ status: 'idle' })
  }, [projectKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const schemesLoaded = [permScheme, notifScheme, secScheme, priScheme].filter((x) => x !== undefined).length
  const assignedSchemes = [permScheme, notifScheme, secScheme, priScheme].filter((x) => x?.name).length
  const customFound = custom.status === 'done' && Array.isArray(custom.report?.categories)
    ? custom.report.categories.reduce((n, c) => n + (c.status === 'ok' && Array.isArray(c.items) ? c.items.length : 0), 0)
    : null

  const tiles: Tile[] = [
    listTile('roles', 'Roles', roles, rolesErr, (rs) => {
      const withMembers = (rs as Role[]).filter((r) => Array.isArray(r.actors) && r.actors.length > 0).length
      return `${withMembers} with members`
    }),
    listTile('versions', 'Versions', versions, versionsErr,
      (vs) => `${(vs as Version[]).filter((v) => !v.released && !v.archived).length} unreleased`),
    listTile('components', 'Components', components, componentsErr, () => 'in this project'),
    automationErr
      ? { key: 'automation', label: 'Automation', value: null, state: 'failed', meta: 'not loaded' }
      : listTile('automation', 'Automation', automation === null ? null : automation.project_rules, false,
        () => (automation && automation.global_count ? `project rules · ${automation.global_count} global` : 'project rules')),
    schemesLoaded < 4
      ? { key: 'schemes', label: 'Schemes', value: null, state: 'loading' }
      : { key: 'schemes', label: 'Schemes', value: assignedSchemes, meta: 'of 4 resolved' },
    custom.status === 'done' && customFound !== null
      ? { key: 'customizations', label: 'Customizations', value: customFound, meta: 'non-standard items' }
      : custom.status === 'error'
        ? { key: 'customizations', label: 'Customizations', value: null, state: 'failed', meta: 'check failed' }
        : custom.status === 'loading'
          ? { key: 'customizations', label: 'Customizations', value: null, state: 'loading' }
          : { key: 'customizations', label: 'Customizations', value: '—', meta: 'run the check' },
  ]

  return (
    <Page
      title={projectName}
      context={(
        <span className="flex min-w-0 items-center gap-2 text-[12px]">
          {projectName !== projectKey && <span className="font-mono text-text-2">{projectKey}</span>}
          <Link
            to="/projects"
            className="inline-flex items-center gap-0.5 text-text-3 transition-colors hover:text-foreground"
          >
            <ChevronLeft aria-hidden className="size-3.5" /> All projects
          </Link>
        </span>
      )}
      actions={<AdminActions projectKey={projectKey} projectName={projectName} onDialog={setDialog} />}
    >
      <StatTiles tiles={tiles} active={tab} onSelect={go} label="Project sections" idPrefix={TAB_PREFIX} panelId={PANEL_ID} />
      <div role="tabpanel" id={PANEL_ID} aria-labelledby={`${TAB_PREFIX}-${tab}`} className="flex min-h-0 flex-1 flex-col">
        {tab === 'roles' && (
          <SectionBoundary key="roles" label="Roles" onRetry={refreshRoles} resetKeys={[roles]}>
            <RolesSection projectKey={projectKey} roles={roles} err={rolesErr} refresh={refreshRoles} onDialog={setDialog} />
          </SectionBoundary>
        )}
        {tab === 'versions' && (
          <SectionBoundary key="versions" label="Versions" onRetry={refreshVersions} resetKeys={[versions]}>
            <VersionsSection projectKey={projectKey} data={versions} err={versionsErr} refresh={refreshVersions} onDialog={setDialog} />
          </SectionBoundary>
        )}
        {tab === 'components' && (
          <SectionBoundary key="components" label="Components" onRetry={refreshComponents} resetKeys={[components]}>
            <ComponentsSection projectKey={projectKey} data={components} err={componentsErr} refresh={refreshComponents} onDialog={setDialog} />
          </SectionBoundary>
        )}
        {tab === 'automation' && (
          <SectionBoundary key="automation" label="Automation" onRetry={refreshAutomation} resetKeys={[automation]}>
            <AutomationSection automation={automation} err={automationErr} refresh={refreshAutomation} onDialog={setDialog} />
          </SectionBoundary>
        )}
        {tab === 'schemes' && (
          <SectionBoundary key="schemes" label="Schemes" onRetry={refreshSchemes}
            resetKeys={[permScheme, notifScheme, secScheme, priScheme]}>
            <SchemesSection
              projectKey={projectKey}
              projectId={projectId}
              permScheme={permScheme}
              notifScheme={notifScheme}
              secScheme={secScheme}
              priScheme={priScheme}
              refresh={refreshSchemes}
              onDialog={setDialog}
            />
          </SectionBoundary>
        )}
        {tab === 'customizations' && (
          <SectionBoundary key="customizations" label="Customizations" resetKeys={[custom]}>
            <ProjectCustomizations state={custom} onRun={() => void runCustomizations()} />
          </SectionBoundary>
        )}
      </div>

      {dialog && (
        <UserActionDialog
          op={dialog.op}
          params={dialog.params}
          title={dialog.title}
          critical={dialog.critical}
          onClose={() => setDialog(null)}
          onDone={dialog.onDone ?? refreshVersions}
        />
      )}
    </Page>
  )
}
